import { useEffect } from "react";
import { getSupabase } from "./supabase-client";
import { useStore } from "../store/useStore";

// Inisialisasi Supabase SECARA SINKRON di level modul agar fragment #access_token
// di URL segera di-parse SEBELUM React Router merender <Navigate replace> dan
// menghilangkan fragment tersebut dari URL.
getSupabase();

export const uploadLocalDataToSupabase = async (userId: string) => {
  const supabase = getSupabase();
  if (!supabase) return;

  const currentState = useStore.getState();
  const currentBabyProfile = currentState.babyProfile;

  const payload = {
    id: userId,
    caregiver_name: currentBabyProfile?.caregiverName || null,
    baby_name: currentBabyProfile?.name || null,
    baby_dob: currentBabyProfile?.dob || null,
    family_size: currentBabyProfile?.familySize || 3,
    city: currentBabyProfile?.city || null,
    last_accessed_date: new Date().toISOString(),
    plan: "free",
  };

  const { error } = await supabase.from("user_profiles").upsert(payload);

  if (error) {
    console.error("Failed to upload local data:", error);
  } else {
    currentState.setUserProfile(payload);
  }
};

export function useAuthSync() {
  const {
    setAuthUser,
    setUserProfile,
    babyProfile,
    setBabyProfile,
    hasOnboarded,
  } = useStore();

  useEffect(() => {
    // Automatically sync to Supabase when babyProfile changes
    const unsub = useStore.subscribe((state, prevState) => {
      // Basic deep equality check for babyProfile to prevent infinite loops
      const profileChanged = JSON.stringify(state.babyProfile) !== JSON.stringify(prevState.babyProfile);
      if (state.authUser && profileChanged && state.hasOnboarded) {
        uploadLocalDataToSupabase(state.authUser.id);
      }
    });
    return unsub;
  }, []);

  useEffect(() => {
    const supabase = getSupabase();
    if (!supabase) return;

    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) {
        setAuthUser(session.user);
        syncUserData(session.user.id);
      } else {
        setAuthUser(null);
        setUserProfile(null);
      }
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "SIGNED_IN" && session?.user) {
        setAuthUser(session.user);
        syncUserData(session.user.id);
      } else if (event === "SIGNED_OUT") {
        setAuthUser(null);
        setUserProfile(null);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const syncUserData = async (userId: string) => {
    const supabase = getSupabase();
    if (!supabase) return;

    try {
      // Fetch profile from supabase
      const { data: profile, error } = await supabase
        .from("user_profiles")
        .select("*")
        .eq("id", userId)
        .single();

      if (error && error.code !== "PGRST116") {
        // PGRST116 is 'not found'
        console.error("Error fetching profile:", error);
      }

      if (profile) {
        // Update local store with Supabase data (Supabase wins)
        setUserProfile(profile);

        // Convert to local BabyProfile schema
        const newBabyProfile = {
          name: profile.baby_name || "",
          dob: profile.baby_dob || "",
          familySize: profile.family_size || 3,
          city: profile.city || "",
          caregiverName: profile.caregiver_name || "",
        };

        // Only update baby profile if it actually has data
        if (profile.baby_name) {
          useStore.getState().setBabyProfile(newBabyProfile);
          useStore.getState().setHasOnboarded(true);
        } else {
          // If they are logged in but have no profile data in supabase, maybe upload local if exists
          uploadLocalDataToSupabase(userId);
        }

        // Update last accessed
        await supabase
          .from("user_profiles")
          .update({ last_accessed_date: new Date().toISOString() })
          .eq("id", userId);
      } else {
        // Profile not found, upload local data to create it
        await uploadLocalDataToSupabase(userId);
      }
    } catch (err) {
      console.error("Error syncing user data:", err);
    }
  };
}
